import React from "react";
import { Button } from "react-bootstrap";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

const RELOAD_KEY = "chunkErrorReloadAt";

// After a deploy, a browser holding an old index.html asks for chunk files
// that no longer exist. A single reload picks up the new build.
const isChunkLoadError = (error: Error) =>
  error?.name === "ChunkLoadError" ||
  /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || "");

const reloadedRecently = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    return Date.now() - last < 10000;
  } catch (e) {
    return true;
  }
};

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("Unhandled render error:", error);
    if (isChunkLoadError(error) && !reloadedRecently()) {
      try {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      } catch (e) {
        return;
      }
      window.location.reload();
    }
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (!hasError) {
      return children;
    }
    return (
      <div className="position-fixed h-100 w-100">
        <div className="p-4 h-100 d-flex flex-column justify-content-center align-items-center">
          <h4>Something went wrong loading the Cyber Portal.</h4>
          <p>
            <span>Try reloading the page. If this keeps happening, please email </span>
            <a href={`mailto:${process.env.REACT_APP_EMAIL_SUPPORT_RECIPIENT}`}>us</a>
            <span>.</span>
          </p>
          <Button onClick={() => { window.location.href = "/"; }}>Reload</Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
